import React, { useState, useEffect, useRef } from "react";
import { Button, Card, CardBody, CardHeader, Container, Modal, ModalBody, ModalFooter, ModalHeader, Form, FormGroup, Input, Label, Dropdown, DropdownToggle, DropdownMenu, DropdownItem, Spinner, Badge, InputGroup, InputGroupText, Col, Row } from "reactstrap";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Toast } from "primereact/toast";
import { FilterMatchMode } from "primereact/api";
import axios from "axios";
import { API_BASE_URL } from "../../../constants";
import Breadcrumbs from "../../../components/Common/Breadcrumb";
import { Edit, MoreVertical, Trash, UserCheck, CloudDownload, Clock, Search, X, SlidersHorizontal, Download } from "lucide-react";
import sampleSheet from "../../../assets/docs/sample-leads.xlsx";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import jsPDF from "jspdf";
import "jspdf-autotable";
import * as XLSX from "xlsx";


const LeadsList = () => {
    const [leads, setLeads] = useState([]);
    const [filteredLeads, setFilteredLeads] = useState([]);
    const [totalRecords, setTotalRecords] = useState(0);
    const [loading, setLoading] = useState(false);
    const [first, setFirst] = useState(0);
    const [rows, setRows] = useState(10);
    const [modal, setModal] = useState(false);
    const [deleteModal, setDeleteModal] = useState(false);
    const [name, setName] = useState("");
    const [email, setEmail] = useState("");
    const [contactPhone, setContactPhone] = useState("");
    const [whatsapp, setWhatsapp] = useState("");
    const [place, setPlace] = useState("");
    const [profession, setProfession] = useState("");
    const [companyName, setCompanyName] = useState("");
    const [requirements, setRequirements] = useState("");
    const [quote, setQuote] = useState("");
    const [socialMedia, setSocial] = useState("");
    const [saleStaff, setSaleStaff] = useState("");
    const [saleStaffId, setSaleStaffId] = useState(null);
    const [remarks, setRemarks] = useState("");
    const [status, setStatus] = useState("new");
    const [followUpDate, setFollowUpDate] = useState("");
    const [selectedLead, setSelectedLead] = useState(null);
    const [leadToDelete, setLeadToDelete] = useState(null);
    const [dropdownOpen, setDropdownOpen] = useState(null);
    const [staffOptions, setStaffOptions] = useState([]);
    // TODO:

    const [leadToConvert, setLeadToConvert] = useState(null);
    const [convertModal, setConvertModal] = useState(false);

    const [statusHistory, setStatusHistory] = useState([]);
    const [historyModalOpen, setHistoryModalOpen] = useState(false);
    const toggleHistoryModal = () => setHistoryModalOpen(!historyModalOpen);

    const [filters, setFilters] = useState({
        global: { value: null, matchMode: FilterMatchMode.CONTAINS },
        name: { value: null, matchMode: FilterMatchMode.CONTAINS },
        email: { value: null, matchMode: FilterMatchMode.CONTAINS },
        contact_phone: { value: null, matchMode: FilterMatchMode.CONTAINS },
        company_name: { value: null, matchMode: FilterMatchMode.CONTAINS },
        requirements: { value: null, matchMode: FilterMatchMode.CONTAINS },
        sale_staff: { value: null, matchMode: FilterMatchMode.CONTAINS },
        status: { value: null, matchMode: FilterMatchMode.EQUALS },
    });

    const [globalFilterValue, setGlobalFilterValue] = useState('');
    const [importModal, setImportModal] = useState(false);
    const [exportModal, setExportModal] = useState(false);
    const [exportFormat, setExportFormat] = useState('excel');
    const [file, setFile] = useState(null);
    const [importLoading, setImportLoading] = useState(false);
    const [showFilters, setShowFilters] = useState(false);
    const [filterStatus, setFilterStatus] = useState('');
    const [filterStaff, setFilterStaff] = useState('');
    const [searchTerm, setSearchTerm] = useState('');
    const [exportLoading, setExportLoading] = useState(false);

    const toggleDropdown = (id) => setDropdownOpen(dropdownOpen === id ? null : id);
    const toast = useRef(null);

    const toggle = () => setModal(!modal);
    const toggleDeleteModal = () => setDeleteModal(!deleteModal);

    const breadcrumbItems = [
        { title: "Sales & Leads", link: "/" },
        { title: "Leads", link: "/admin/salesandleads/leads" },
    ];

    const nameBodyTemplate = (rowData) => {
        return (
            <div>
                <div className="fw-bold">{rowData.name}</div>
                <div className="text-muted small">{rowData.email}</div>
                <div className="text-muted small">{rowData.contact_phone}</div>
            </div>
        );
    };

    const statusOptions = [
        { label: "Contacted", value: "contacted" },
        { label: "Interested", value: "interested" },
        { label: "Follow Up Scheduled", value: "follow_up_scheduled" },
        { label: "Negotiation", value: "negotiation" },
        { label: "Proposal Sent", value: "proposal_sent" },
        { label: "Cold Lead", value: "cold_lead" },
        { label: "No Response", value: "no_response" },
        { label: "Not Interested", value: "not_interested" }
    ];

    const fetchLeads = React.useCallback(async () => {
        setLoading(true);
        try {
            const response = await axios.get(`${API_BASE_URL}/leads/list`, {
                headers: { Authorization: "Bearer " + localStorage.getItem("token") },
            });
            const leadsData = response.data.data || [];
            setLeads(leadsData);
            setFilteredLeads(leadsData);
            setTotalRecords(leadsData.length);
        } catch (error) {
            console.error("Error fetching leads:", error);
            toast.current?.show({
                severity: 'error',
                summary: 'Error',
                detail: error.response?.data?.message || 'Failed to fetch leads',
                life: 3000
            });
        }
        setLoading(false);
    }, []);

    const fetchStaff = React.useCallback(async () => {
        try {
            const response = await axios.get(`${API_BASE_URL}/staffs/sale-staffs-list`, {
                headers: { Authorization: "Bearer " + localStorage.getItem("token") },
            });
            setStaffOptions(response.data.data || []);
        } catch (error) {
            console.error("Error fetching staff:", error);
            toast.current?.show({
                severity: 'error',
                summary: 'Error',
                detail: error.response?.data?.message || 'Failed to fetch staff',
                life: 3000
            });
        }
    }, []);

    useEffect(() => {
        fetchLeads();
        fetchStaff();
    }, [fetchLeads, fetchStaff]);

    // Apply filters whenever filter criteria changes
    useEffect(() => {
        applyFilters();
    }, [searchTerm, filterStatus, filterStaff, leads, globalFilterValue, applyFilters]);

    // Function to apply all filters
    const applyFilters = React.useCallback(() => {
        let filtered = [...leads];

        // Apply status filter
        if (filterStatus) {
            filtered = filtered.filter(lead => lead.status === filterStatus);
        }

        // Apply staff filter
        if (filterStaff) {
            // eslint-disable-next-line eqeqeq
            filtered = filtered.filter(lead => lead.sale_staff_id == filterStaff);
        }

        // Apply search term (advanced search across all fields)
        if (searchTerm) {
            const term = searchTerm.toLowerCase();
            filtered = filtered.filter(lead =>
                (lead.name && lead.name.toLowerCase().includes(term)) ||
                (lead.email && lead.email.toLowerCase().includes(term)) ||
                (lead.contact_phone && lead.contact_phone.toLowerCase().includes(term)) ||
                (lead.company_name && lead.company_name.toLowerCase().includes(term)) ||
                (lead.requirements && lead.requirements.toLowerCase().includes(term)) ||
                (lead.sale_staff && lead.sale_staff.toLowerCase().includes(term)) ||
                (lead.place && lead.place.toLowerCase().includes(term)) ||
                (lead.profession && lead.profession.toLowerCase().includes(term)) ||
                (lead.whatsapp && lead.whatsapp.toLowerCase().includes(term))
            );
        }

        // Apply global filter (standard search box)
        if (globalFilterValue) {
            const term = globalFilterValue.toLowerCase();
            filtered = filtered.filter(lead =>
                (lead.name && lead.name.toLowerCase().includes(term)) ||
                (lead.email && lead.email.toLowerCase().includes(term)) ||
                (lead.contact_phone && lead.contact_phone.toLowerCase().includes(term)) ||
                (lead.company_name && lead.company_name.toLowerCase().includes(term))
            );
        }

        // Update filtered leads and total records
        setFilteredLeads(filtered);
        setTotalRecords(filtered.length);
    }, [leads, filterStatus, filterStaff, searchTerm, globalFilterValue]);

    const resetFilters = () => {
        setFilterStatus('');
        setFilterStaff('');
        setSearchTerm('');
        setGlobalFilterValue('');

        // Reset PrimeReact filters
        let _filters = { ...filters };
        Object.keys(_filters).forEach(key => {
            _filters[key].value = null;
        });
        setFilters(_filters);
    };

    const onPageChange = (event) => {
        setFirst(event.first);
        setRows(event.rows);
    };



    const handleSubmit = async (e) => {
        e.preventDefault();
        const data = {
            name,
            email,
            contact_phone: contactPhone,
            whatsapp,
            place,
            profession,
            company_name: companyName,
            requirements,
            sale_staff: saleStaff,
            sale_staff_id: saleStaffId,
            remarks,
            quote,
            social_media: socialMedia,
            status,
            follow_up_date: followUpDate,
            added_by: localStorage.getItem("user_id"),
        };

        setLoading(true);
        try {
            if (selectedLead) {
                await axios.post(`${API_BASE_URL}/leads/update/${selectedLead.id}`, data, {
                    headers: { Authorization: "Bearer " + localStorage.getItem("token") },
                });
                toast.current.show({ severity: 'success', summary: 'Updated', detail: 'Lead updated successfully', life: 3000 });
            } else {
                await axios.post(`${API_BASE_URL}/leads/add`, data, {
                    headers: { Authorization: "Bearer " + localStorage.getItem("token") },
                });
                toast.current.show({ severity: 'success', summary: 'Added', detail: 'Lead added successfully', life: 3000 });
            }
            resetForm();
            toggle();
            fetchLeads();
        } catch (error) {
            console.error("Error details:", error.response?.data);
            toast.current?.show({
                severity: 'error',
                summary: 'Error',
                detail: error.response?.data?.message || 'Failed to save lead',
                life: 3000
            });
        }
        setLoading(false);
    };

    const resetForm = () => {
        setName("");
        setEmail("");
        setContactPhone("");
        setWhatsapp("");
        setPlace("");
        setProfession("");
        setCompanyName("");
        setRequirements("");
        setSaleStaff("");
        setSaleStaffId("");
        setRemarks("");
        setStatus("new");
        setSelectedLead(null);
        setFollowUpDate("");
    };

    const handleDelete = async () => {
        if (!leadToDelete) return;
        try {
            await axios.delete(`${API_BASE_URL}/leads/delete/${leadToDelete.id}`, {
                headers: { Authorization: "Bearer " + localStorage.getItem("token") },
            });
            toast.current?.show({
                severity: "success",
                summary: "Deleted",
                detail: "Lead deleted successfully",
                life: 3000
            });
            fetchLeads();
        } catch (error) {
            toast.current?.show({
                severity: "error",
                summary: "Error",
                detail: "Failed to delete lead",
                life: 3000
            });
        }
        setLeadToDelete(null);
        toggleDeleteModal();
    };

    const statusBodyTemplate = (rowData) => {
        const getStatusClass = (status) => {
            switch (status) {
                case 'contacted': return 'success';
                case 'interested': return 'warning';
                case 'follow_up_scheduled': return 'info';
                case 'negotiation': return 'secondary';
                case 'proposal_sent': return 'success';
                case 'cold_lead': return 'danger';
                case 'no_response': return 'danger';
                case 'not_interested': return 'danger';
                default: return 'info';
            }
        };

        const statusLabel = statusOptions.find(opt => opt.value === rowData.status)?.label || rowData.status;
        const statusClass = getStatusClass(rowData.status);

        return (
            <span className={`badge bg-${statusClass}-subtle text-${statusClass} text-uppercase`}>
                {statusLabel}
            </span>
        );
    };

    const convertToClientTemplate = (rowData) => {
        if (rowData.is_converted_to_client) {
            return <Badge color="success" className="p-2">Converted</Badge>;
        }

        return (
            <Button
                color="primary"
                size="sm"
                onClick={() => {
                    setLeadToConvert(rowData);
                    setConvertModal(true);
                }}
            >
                <UserCheck size={16} className="me-1" />
                Convert
            </Button>
        );
    };



    const handleConvertConfirmation = async () => {
        if (!leadToConvert) return;

        try {
            setLoading(true);
            const response = await axios.post(
                `${API_BASE_URL}/clients/convert-lead/${leadToConvert.id}`,
                {},
                {
                    headers: {
                        Authorization: "Bearer " + localStorage.getItem("token")
                    }
                }
            );

            // Update local state immediately
            setLeads(prevLeads =>
                prevLeads.map(item =>
                    item.id === leadToConvert.id
                        ? { ...item, is_converted_to_client: true }
                        : item
                )
            );

            toast.current.show({
                severity: "success",
                summary: "Converted",
                detail: response.data.message || "Lead converted to client successfully",
                life: 3000
            });

            // Refresh the leads list after conversion
            fetchLeads();
        } catch (error) {
            console.error("Conversion error:", error);
            toast.current.show({
                severity: "error",
                summary: "Error",
                detail: error.response?.data?.message || "Failed to convert lead to client",
                life: 3000
            });
        } finally {
            setLoading(false);
            setLeadToConvert(null);
            setConvertModal(false);
        }
    };

    const emptyFieldTemplate = (field) => {
        return field ? field : <span className="text-muted">..</span>;
    };

    const handleImport = async () => {
        if (!file) return;

        setImportLoading(true);
        const formData = new FormData();
        formData.append('file', file);

        try {
            const response = await axios.post(`${API_BASE_URL}/leads/import`, formData, {
                headers: {
                    'Authorization': 'Bearer ' + localStorage.getItem("token"),
                    'Content-Type': 'multipart/form-data'
                }
            });

            toast.current.show({
                severity: 'success',
                summary: 'Success',
                detail: response.data.message || 'Leads imported successfully',
                life: 3000
            });

            // Refresh the leads list
            fetchLeads();
            setImportModal(false);
            setFile(null);
        } catch (error) {
            console.error("Import error:", error);
            toast.current.show({
                severity: 'error',
                summary: 'Error',
                detail: error.response?.data?.message || 'Failed to import leads',
                life: 3000
            });
        } finally {
            setImportLoading(false);
        }
    };


    const handleExport = async () => {
        setExportLoading(true);

        try {
            // Prepare data for export
            const prepareExportData = (data) => {
                return data.map(lead => ({
                    'Name': lead.name || '',
                    'Email': lead.email || '',
                    'Phone': lead.contact_phone || '',  // Changed from lead.phone
                    'WhatsApp': lead.whatsapp || '',
                    'Location': lead.place || '',
                    'Profession': lead.profession || '',
                    'Company Name': lead.company_name || '',
                    'Requirements': lead.requirements || '',
                    'Quote': lead.quote || '',
                    'Sale Staff': lead.sale_staff || '',
                    'Status': lead.status || '',
                    'Follow Up Date': lead.follow_up_date ?
                        new Date(lead.follow_up_date).toLocaleDateString() : '',
                    'Remarks': lead.remarks || '',
                    'Social Media': lead.social_media || '',
                    'Created Date': new Date(lead.created_at).toLocaleDateString(),
                    'Last Updated': new Date(lead.updated_at).toLocaleDateString()
                }));
            };

            const dataToExport = prepareExportData(filteredLeads);

            if (exportFormat === 'excel') {
                exportToExcel(dataToExport);
            } else if (exportFormat === 'csv') {
                exportToCSV(dataToExport);
            } else if (exportFormat === 'pdf') {
                exportToPDF(dataToExport);
            }

            toast.current.show({
                severity: 'success',
                summary: 'Success',
                detail: `Leads exported as ${exportFormat.toUpperCase()} successfully`,
                life: 3000
            });
        } catch (error) {
            console.error("Export error:", error);
            toast.current.show({
                severity: 'error',
                summary: 'Error',
                detail: 'Failed to export leads',
                life: 3000
            });
        } finally {
            setExportLoading(false);
            setExportModal(false);
        }
    };

    const exportToExcel = (data) => {
        try {
            const workbook = XLSX.utils.book_new();
            const worksheet = XLSX.utils.json_to_sheet(data);
            XLSX.utils.book_append_sheet(workbook, worksheet, "Leads");

            // Generate file name with current date
            const fileName = `leads_export_${new Date().toISOString().slice(0, 10)}.xlsx`;
            XLSX.writeFile(workbook, fileName);
        } catch (error) {
            console.error("Excel export error:", error);
            throw error;
        }
    };

    const exportToCSV = (data) => {
        try {
            const worksheet = XLSX.utils.json_to_sheet(data);
            const csv = XLSX.utils.sheet_to_csv(worksheet);

            // Create download link
            const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
            const link = document.createElement("a");
            const url = URL.createObjectURL(blob);
            link.setAttribute("href", url);
            link.setAttribute("download", `leads_export_${new Date().toISOString().slice(0, 10)}.csv`);
            link.style.visibility = 'hidden';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        } catch (error) {
            console.error("CSV export error:", error);
            throw error;
        }
    };

    const exportToPDF = (data) => {
        const doc = new jsPDF('landscape');
        const date = new Date().toLocaleDateString();
        const time = new Date().toLocaleTimeString();

        // Add title and metadata
        doc.setFontSize(18);
        doc.text("Lead Management Report", 14, 15);
        doc.setFontSize(10);
        doc.text(`Generated on ${date} at ${time}`, 14, 28);

        // Add summary statistics
        let yPos = 36;
        doc.setFont(undefined, 'bold');
        doc.text("Summary Statistics", 14, yPos);
        doc.setFont(undefined, 'normal');
        yPos += 6;

        doc.text(`Total Leads: ${data.length}`, 20, yPos);
        yPos += 6;


        // Status counts
        const statusCounts = {};
        data.forEach(lead => {
            statusCounts[lead.Status] = (statusCounts[lead.Status] || 0) + 1;
        });

        Object.entries(statusCounts).forEach(([status, count]) => {
            doc.text(`${status}: ${count}`, 20, yPos);
            yPos += 6;
        });

        // Add client details table
        doc.setFont(undefined, 'bold');
        doc.text("Leads Details", 14, yPos);
        yPos += 8;

        doc.autoTable({
            startY: yPos,
            head: [['Name', 'Email', 'Phone', 'Company Name', 'Sale Staff', 'Added date', 'follow_up_date', 'Country', 'State', 'City', 'Address', 'Created Date', 'Status']],
            body: data.map(lead => [
                lead.name,
                lead.email,
                lead.phone,
                lead.company_name,
                lead.sale_staff,
                lead.added_date,
                lead.follow_up_date,
                lead.country,
                lead.state,
                lead.city,
                lead.address,
                lead.status
            ]),
            theme: 'striped',
            headStyles: { fillColor: [41, 128, 185] },
            columnStyles: {
                0: { cellWidth: 25 },
                1: { cellWidth: 30 },
                2: { cellWidth: 20 },
                3: { cellWidth: 25 },
                4: { cellWidth: 25 },
                5: { cellWidth: 25 },
                6: { cellWidth: 25 },
                7: { cellWidth: 25 },
                8: { cellWidth: 30 },
                9: { cellWidth: 25 },
                10: { cellWidth: 'auto' }
            },
            margin: { top: 25, right: 10, left: 10 },
            didDrawPage: function (data) {
                // Add page number at the bottom
                doc.setFontSize(8);
                doc.text(
                    'Page ' + doc.internal.getCurrentPageInfo().pageNumber,
                    data.settings.margin.left,
                    doc.internal.pageSize.height - 10
                );
            }
        });

        // Save the PDF
        doc.save(`lead_report_${new Date().toISOString().slice(0, 10)}.pdf`);
    };


    return (
        <React.Fragment>
            <div className="page-content">
                <Container fluid>
                    <Breadcrumbs title="Leads" breadcrumbItems={breadcrumbItems} />
                    <Card>
                        <CardHeader className="d-flex justify-content-between align-items-center">
                            <h4 className="card-title mb-0">Leads List</h4>
                            {/* Mobile view - stacked layout */}
                            <div className="d-md-none mt-2">
                                <div className="d-flex flex-column gap-2">
                                    <div className="d-flex gap-2">
                                        <Button
                                            color="success"
                                            onClick={() => setShowFilters(!showFilters)}
                                            className="me-2"
                                        >
                                            <SlidersHorizontal size={13} /> {showFilters ? 'Hide' : 'Show'} Filters
                                        </Button>
                                        <Button color="primary" onClick={toggle} className="flex-grow-1">
                                            <i className="mdi mdi-plus mr-1"></i>Add
                                        </Button>
                                        <Button color="success" onClick={() => setImportModal(true)} className="flex-grow-1">
                                            <i className="mdi mdi-file-import mr-1"></i>Import
                                        </Button>
                                        <Button color="info" onClick={() => setExportModal(true)} className="flex-grow-1">
                                            <i className="mdi mdi-file-export mr-1"></i>Export
                                        </Button>
                                    </div>
                                </div>
                            </div>

                            {/* Desktop view - horizontal layout */}
                            <div className="d-none d-md-flex align-items-center gap-3">
                                <Button
                                    color="success"
                                    onClick={() => setShowFilters(!showFilters)}
                                >
                                    <SlidersHorizontal size={14} /> {showFilters ? 'Hide' : 'Show'} Filters
                                </Button>
                                <Button color="primary" onClick={() => { toggle(); setSelectedLead(null); }}>
                                    <i className="mdi mdi-plus mr-1"></i>Add Lead
                                </Button>
                                <Button color="success" onClick={() => setImportModal(true)}>
                                    <CloudDownload size={14} className="me-1" />Import Leads
                                </Button>
                                <Button color="info" onClick={() => setExportModal(true)}>
                                    <Download size={14} className="me-1" />Export Leads
                                </Button>
                            </div>
                        </CardHeader>

                        {/* Enhanced Filter Section */}
                        {showFilters && (
                            <CardBody className="border-bottom pb-3">
                                <Row className="mb-3">
                                    <Col md={4}>
                                        <FormGroup>
                                            <Label>Advanced Search</Label>
                                            <InputGroup>
                                                <InputGroupText>
                                                    <Search size={14} />
                                                </InputGroupText>
                                                <Input
                                                    type="text"
                                                    placeholder="Search across all fields..."
                                                    value={searchTerm}
                                                    onChange={(e) => setSearchTerm(e.target.value)}
                                                />
                                                {searchTerm && (
                                                    <Button
                                                        color="link"
                                                        onClick={() => setSearchTerm('')}
                                                        className="text-muted"
                                                    >
                                                        <X size={14} />
                                                    </Button>
                                                )}
                                            </InputGroup>
                                            {/* <small className="text-muted">
                                                Searches across name, email, phone, company, requirements, etc.
                                            </small> */}
                                        </FormGroup>
                                    </Col>
                                    <Col md={4}>
                                        <FormGroup>
                                            <Label>Status Filter</Label>
                                            <Input
                                                type="select"
                                                value={filterStatus}
                                                onChange={(e) => setFilterStatus(e.target.value)}
                                            >
                                                <option value="">All Statuses</option>
                                                {statusOptions.map(option => (
                                                    <option key={option.value} value={option.value}>
                                                        {option.label}
                                                    </option>
                                                ))}
                                            </Input>
                                        </FormGroup>
                                    </Col>
                                    <Col md={4}>
                                        <FormGroup>
                                            <Label>Sales Staff Filter</Label>
                                            <Input
                                                type="select"
                                                value={filterStaff}
                                                onChange={(e) => setFilterStaff(e.target.value)}
                                            >
                                                <option value="">All Staff</option>
                                                {staffOptions.map(staff => (
                                                    <option key={staff.user_id} value={staff.user_id}>
                                                        {staff.name}
                                                    </option>
                                                ))}
                                            </Input>
                                        </FormGroup>
                                    </Col>
                                </Row>
                                {/* <div className="d-flex justify-content-end">
                                    <Button 
                                        color="link" 
                                        onClick={resetFilters}
                                        className="text-danger"
                                    >
                                        <X size={14} className="me-1" />
                                        Clear All Filters
                                    </Button>
                                </div> */}
                            </CardBody>
                        )}

                        <CardBody>
                            {/* Filter status indicator */}
                            {(searchTerm || filterStatus || filterStaff || globalFilterValue) && (
                                <div className="mb-3 d-flex align-items-center">
                                    <span className="text-muted me-2">Active filters:</span>
                                    <div className="d-flex flex-wrap gap-2">
                                        {globalFilterValue && (
                                            <Badge color="light" className="d-flex align-items-center p-2 me-1">
                                                Quick search: {globalFilterValue}
                                                <Button
                                                    close
                                                    size="sm"
                                                    onClick={() => setGlobalFilterValue('')}
                                                />
                                            </Badge>
                                        )}
                                        {searchTerm && (
                                            <Badge color="light" className="d-flex align-items-center p-2 me-1">
                                                Advanced search: {searchTerm}
                                                <Button
                                                    close
                                                    size="sm"
                                                    onClick={() => setSearchTerm('')}
                                                />
                                            </Badge>
                                        )}
                                        {filterStatus && (
                                            <Badge color="light" className="d-flex align-items-center p-2 me-1">
                                                Status: {statusOptions.find(opt => opt.value === filterStatus)?.label || filterStatus}
                                                <Button
                                                    close
                                                    size="sm"
                                                    onClick={() => setFilterStatus('')}
                                                />
                                            </Badge>
                                        )}
                                        {filterStaff && (
                                            <Badge color="light" className="d-flex align-items-center p-2 me-1">
                                                Staff: {staffOptions.find(staff => String(staff.user_id) === String(filterStaff))?.name || filterStaff}
                                                <Button
                                                    close
                                                    size="sm"
                                                    onClick={() => setFilterStaff('')}
                                                />
                                            </Badge>
                                        )}
                                    </div>
                                    <Button
                                        color="link"
                                        size="sm"
                                        className="ms-auto text-danger"
                                        onClick={resetFilters}
                                    >
                                        Clear All
                                    </Button>
                                </div>
                            )}

                            <DataTable
                                value={filteredLeads}
                                loading={loading}
                                stripedRows
                                paginator
                                rows={rows}
                                first={first}
                                totalRecords={totalRecords}
                                onPage={onPageChange}
                                rowsPerPageOptions={[5, 10, 25, 50]}
                                emptyMessage="No leads found"
                                className="mt-3"
                            >
                                <Column header="SI.No" body={(rowData, { rowIndex }) => rowIndex + 1} style={{ width: '60px' }} />
                                <Column
                                    header="Leads"
                                    body={nameBodyTemplate}
                                    sortable
                                    sortField="name"
                                    style={{ minWidth: '200px' }}
                                />
                                <Column
                                    header="Company"
                                    field="company_name"
                                    sortable
                                    body={(rowData) => emptyFieldTemplate(rowData.company_name)}
                                    style={{ minWidth: '150px' }}
                                />
                                <Column
                                    header="Requirements"
                                    field="requirements"
                                    sortable
                                    body={(rowData) => emptyFieldTemplate(rowData.requirements)}
                                    style={{ minWidth: '200px' }}
                                />
                                <Column
                                    header="Staff"
                                    body={(rowData) => emptyFieldTemplate(rowData.sale_staff)}
                                    style={{ minWidth: '120px' }}
                                />
                                <Column
                                    header="Status"
                                    body={statusBodyTemplate}
                                    sortable
                                    field="status"
                                    style={{ minWidth: '120px' }}
                                />
                                <Column
                                    header="Added Date"
                                    body={(rowData) => {
                                        const date = new Date(rowData.created_at);
                                        return date.toLocaleDateString('en-US', {
                                            year: 'numeric',
                                            month: 'short',
                                            day: 'numeric'
                                        });
                                    }}
                                    sortable
                                    field="created_at"
                                    style={{ minWidth: '120px' }}
                                />
                                <Column
                                    header="Follow Up Date"
                                    body={(rowData) => {
                                        if (!rowData.follow_up_date) {
                                            return <span className="text-muted">Not set</span>;
                                        }

                                        // Format the date as "MMM DD, YYYY" (e.g., "Jan 15, 2023")
                                        const date = new Date(rowData.follow_up_date);
                                        console.log(date);
                                        return date.toLocaleDateString('en-US', {
                                            year: 'numeric',
                                            month: 'short',
                                            day: 'numeric'
                                        });
                                    }}
                                    sortable
                                    sortField="follow_up_date"
                                    style={{ minWidth: '120px' }}
                                />
                                <Column
                                    header="Converted"
                                    body={convertToClientTemplate}
                                    style={{ width: '120px' }}
                                />
                                <Column
                                    header="Actions"
                                    body={(rowData) => (
                                        <Dropdown
                                            isOpen={dropdownOpen === rowData.id}
                                            toggle={() => toggleDropdown(rowData.id)}
                                        >
                                            <DropdownToggle tag="button" className="btn btn-light">
                                                <MoreVertical size={20} />
                                            </DropdownToggle>
                                            <DropdownMenu>
                                                <DropdownItem
                                                    onClick={() => {
                                                        setSelectedLead(rowData);
                                                        setStatusHistory(rowData.statuses || []);
                                                        toggleHistoryModal();
                                                    }}
                                                >
                                                    <Clock size={16} className="me-2" />
                                                    View History
                                                </DropdownItem>
                                                <DropdownItem
                                                    onClick={() => {
                                                        setSelectedLead(rowData);
                                                        setName(rowData.name);
                                                        setEmail(rowData.email);
                                                        setContactPhone(rowData.contact_phone);
                                                        setWhatsapp(rowData.whatsapp);
                                                        setPlace(rowData.place);
                                                        setProfession(rowData.profession);
                                                        setCompanyName(rowData.company_name);
                                                        setRequirements(rowData.requirements);
                                                        setSaleStaff(rowData.sale_staff);
                                                        setSaleStaffId(rowData.sale_staff_id);
                                                        setRemarks(rowData.remarks);
                                                        setStatus(rowData.status);
                                                        setQuote(rowData.quote);
                                                        setSocial(rowData.social_media);
                                                        setFollowUpDate(rowData.follow_up_date || "");
                                                        toggle();
                                                    }}
                                                >
                                                    <Edit size={16} className="me-2" />
                                                    Edit
                                                </DropdownItem>
                                                <DropdownItem
                                                    onClick={() => {
                                                        setLeadToDelete(rowData);
                                                        toggleDeleteModal();
                                                    }}
                                                    className="text-danger"
                                                >
                                                    <Trash size={16} className="me-2" />
                                                    Delete
                                                </DropdownItem>
                                            </DropdownMenu>
                                        </Dropdown>
                                    )}
                                    style={{ width: '80px' }}
                                />
                            </DataTable>
                        </CardBody>
                    </Card>
                </Container>
            </div>

            <Modal isOpen={modal} centered toggle={toggle} size="lg" >
                <Form onSubmit={handleSubmit}>
                    <ModalHeader toggle={() => { toggle(); resetForm() }}>{selectedLead ? 'Edit Lead' : 'Add Lead'}</ModalHeader>
                    <ModalBody>
                        <div className="row">
                            <div className="col-md-4">
                                <FormGroup>
                                    <Label>Name*</Label>
                                    <Input
                                        type="text"
                                        value={name}
                                        placeholder="Enter name"
                                        onChange={(e) => setName(e.target.value)}
                                        required
                                    />
                                </FormGroup>
                            </div>
                            <div className="col-md-4">
                                <FormGroup>
                                    <Label>Email</Label>
                                    <Input
                                        type="email"
                                        value={email}
                                        placeholder="Enter email"
                                        onChange={(e) => setEmail(e.target.value)}
                                    />
                                </FormGroup>
                            </div>
                            <div className="col-md-4">
                                <FormGroup>
                                    <Label>Contact Phone*</Label>
                                    <Input
                                        type="text"
                                        value={contactPhone}
                                        placeholder="Enter contact phone"
                                        onChange={(e) => setContactPhone(e.target.value)}
                                        required
                                    />
                                </FormGroup>
                            </div>
                        </div>

                        <div className="row">
                            <div className="col-md-4">
                                <FormGroup>
                                    <Label>WhatsApp</Label>
                                    <Input
                                        type="text"
                                        value={whatsapp}
                                        placeholder="Enter WhatsApp"
                                        onChange={(e) => setWhatsapp(e.target.value)}
                                    />
                                </FormGroup>
                            </div>
                            <div className="col-md-4">
                                <FormGroup>
                                    <Label>Location</Label>
                                    <Input
                                        type="text"
                                        value={place}
                                        placeholder="Enter location"
                                        onChange={(e) => setPlace(e.target.value)}
                                    />
                                </FormGroup>
                            </div>
                            <div className="col-md-4">
                                <FormGroup>
                                    <Label>Profession/Industry</Label>
                                    <Input
                                        type="text"
                                        value={profession}
                                        placeholder="Enter profession/industry"
                                        onChange={(e) => setProfession(e.target.value)}
                                    />
                                </FormGroup>
                            </div>
                        </div>

                        {/* <div className="row">
                            
                        </div> */}

                        <div className="row">
                            <div className="col-md-4">
                                <FormGroup>
                                    <Label>Company Name</Label>
                                    <Input
                                        type="text"
                                        value={companyName}
                                        placeholder="Enter company name"
                                        onChange={(e) => setCompanyName(e.target.value)}
                                    />
                                </FormGroup>
                            </div>
                            <div className="col-md-4">
                                <FormGroup>
                                    <Label>Quote</Label>
                                    <Input
                                        type="number"
                                        value={quote}
                                        placeholder="Enter Quote"
                                        onChange={(e) => setQuote(e.target.value)}
                                    />
                                </FormGroup>
                            </div>
                            <div className="col-md-4">
                                <FormGroup>
                                    <Label>Status</Label>
                                    <Input
                                        type="select"
                                        value={status}
                                        onChange={(e) => setStatus(e.target.value)}
                                    >
                                        <option value="">Select Status</option>
                                        {statusOptions.map(option => (
                                            <option key={option.value} value={option.value}>
                                                {option.label}
                                            </option>
                                        ))}
                                    </Input>
                                </FormGroup>
                            </div>
                            <div className="col-md-4">
                                <FormGroup>
                                    <Label>Staff</Label>
                                    <Input
                                        type="select"
                                        value={saleStaffId?.toString()} // Ensure it's always a string
                                        onChange={(e) => {
                                            const selectedId = e.target.value;
                                            const selectedStaff = staffOptions.find(
                                                staff => staff.user_id.toString() === selectedId
                                            );
                                            setSaleStaff(selectedStaff ? selectedStaff.name : "");
                                            setSaleStaffId(selectedId);
                                        }}
                                    >
                                        <option value="">Select Staff</option>
                                        {staffOptions.map(staff => (
                                            <option key={staff.user_id} value={staff.user_id.toString()}>
                                                {staff.name}
                                            </option>
                                        ))}
                                    </Input>
                                </FormGroup>
                            </div>
                            <div className="col-md-4">
                                <FormGroup>
                                    <Label>Follow Up Date</Label>
                                    <Input
                                        type="date"
                                        value={followUpDate ? followUpDate.split('T')[0] : ''}
                                        onChange={(e) => setFollowUpDate(e.target.value)}
                                    />
                                </FormGroup>
                            </div>
                        </div>

                        <div className="row">
                            <div className="col-md-6">
                                <FormGroup>
                                    <Label>Requirements*</Label>
                                    <Input
                                        type="textarea"
                                        value={requirements}
                                        onChange={(e) => setRequirements(e.target.value)}
                                        required
                                    />
                                </FormGroup>
                            </div>
                            <div className="col-md-6">
                                <FormGroup>
                                    <Label>Notes</Label>
                                    <Input
                                        type="textarea"
                                        value={remarks}
                                        onChange={(e) => setRemarks(e.target.value)}
                                    />
                                </FormGroup>
                            </div>
                            <div className="col-md-12">
                                <FormGroup>
                                    <Label>Social media</Label>
                                    <ReactQuill
                                        theme="snow"
                                        value={socialMedia}
                                        onChange={setSocial}
                                        style={{ height: '100px', marginBottom: '5px' }}
                                    />
                                </FormGroup>
                            </div>
                        </div>
                    </ModalBody>
                    <ModalFooter>
                        <Button color="secondary" onClick={() => {
                            toggle(); // Close the modal
                            resetForm(); // Reset the form fields
                        }}>
                            Cancel
                        </Button>
                        <Button color="primary" type="submit" disabled={loading}>
                            {selectedLead ? "Update" : "Add"}
                            {loading && <Spinner className="ms-1" size="sm">Loading...</Spinner>}
                        </Button>
                    </ModalFooter>
                </Form>
            </Modal>

            <Modal isOpen={deleteModal} centered toggle={toggleDeleteModal}>
                <ModalHeader toggle={toggleDeleteModal}>Confirm Delete</ModalHeader>
                <ModalBody>
                    Are you sure you want to delete this lead?
                </ModalBody>
                <ModalFooter>
                    <Button color="secondary" onClick={toggleDeleteModal}>Cancel</Button>
                    <Button color="danger" onClick={handleDelete} disabled={loading}>
                        Delete
                        {loading && <Spinner className="ms-1" size="sm">Loading...</Spinner>}
                    </Button>
                </ModalFooter>
            </Modal>
            <Modal isOpen={historyModalOpen} centered toggle={toggleHistoryModal} size="lg">
                <ModalHeader toggle={toggleHistoryModal}>
                    {selectedLead?.name}
                </ModalHeader>
                <ModalBody>
                    {statusHistory.length > 0 ? (
                        <div className="table-responsive">
                            <table className="table table-striped table-bordered align-middle">
                                <thead className="table-success">
                                    <tr>
                                        <th>#</th>
                                        <th>Status</th>
                                        <th>Remarks</th>
                                        <th>Added By</th>
                                        <th>Date</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {statusHistory.map((item, index) => (
                                        <tr key={index}>
                                            <td>{index + 1}</td>
                                            <td>
                                                <span className="badge bg-secondary text-uppercase">
                                                    {item.status}
                                                </span>
                                            </td>
                                            <td>{item.remarks}</td>
                                            <td>{item.added_by || "N/A"}</td>
                                            <td>{new Date(item.created_at).toLocaleString('en-US', { year: 'numeric', month: 'long', day: 'numeric', hour: 'numeric', minute: 'numeric', hour12: true })}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    ) : (
                        <p className="text-muted">No status history available.</p>
                    )}
                </ModalBody>
            </Modal>

            {/* leads export */}
            <Modal isOpen={exportModal} centered toggle={() => setExportModal(false)}>
                <ModalHeader toggle={() => setExportModal(false)}>Export Leads</ModalHeader>
                <ModalBody>
                    <Form>
                        <FormGroup>
                            <Label>Export Format</Label>
                            <Input
                                type="select"
                                value={exportFormat}
                                onChange={(e) => setExportFormat(e.target.value)}
                            >
                                <option value="excel">Excel (.xlsx)</option>
                                <option value="csv">CSV (.csv)</option>
                                <option value="pdf">PDF (.pdf)</option>
                            </Input>
                        </FormGroup>

                        <div className="mt-3 alert alert-info">
                            <small>
                                <strong>Note:</strong> The export will include all visible columns in the table.
                                For PDF exports, some columns may be abbreviated to fit the page.
                            </small>
                        </div>
                    </Form>
                </ModalBody>
                <ModalFooter>
                    <Button color="secondary" onClick={() => setExportModal(false)}>
                        Cancel
                    </Button>
                    <Button
                        color="primary"
                        onClick={handleExport}
                        disabled={exportLoading}
                    >
                        {exportLoading ? 'Exporting...' : 'Export'}
                        {exportLoading && <Spinner className="ms-1" size="sm">Loading...</Spinner>}
                    </Button>
                </ModalFooter>
            </Modal>

            {/* Convert to Client Confirmation Modal */}
            <Modal isOpen={convertModal} centered toggle={() => setConvertModal(false)}>
                <ModalHeader toggle={() => setConvertModal(false)}>Confirm Conversion</ModalHeader>
                <ModalBody>
                    Are you sure you want to convert this lead to a client?
                    {leadToConvert && (
                        <div className="mt-3">
                            <strong>Lead Details:</strong>
                            <ul className="ps-3">
                                <li>Name: {leadToConvert.name}</li>
                                <li>Company: {leadToConvert.company_name || 'N/A'}</li>
                                <li>Contact: {leadToConvert.contact_phone}</li>
                            </ul>
                        </div>
                    )}
                </ModalBody>
                <ModalFooter>
                    <Button color="secondary" onClick={() => setConvertModal(false)}>
                        Cancel
                    </Button>
                    <Button
                        color="primary"
                        onClick={handleConvertConfirmation}
                        disabled={loading}
                    >
                        {loading ? 'Converting...' : 'Confirm Conversion'}
                        {loading && <Spinner className="ms-1" size="sm">Loading...</Spinner>}
                    </Button>
                </ModalFooter>
            </Modal>

            <Modal isOpen={importModal} centered toggle={() => setImportModal(!importModal)}>
                <ModalHeader toggle={() => setImportModal(!importModal)}>
                    Import Leads from File
                    <span className="text-end ms-2">
                        <a href={sampleSheet} download className="text-primary">
                            Download Sample Sheet <CloudDownload size={12} />
                        </a>
                    </span>
                </ModalHeader>
                <ModalBody>
                    <Form>
                        <FormGroup>
                            <Label>Select File (Excel, CSV, or PDF)</Label>
                            <Input
                                type="file"
                                accept=".xlsx, .xls, .csv, .pdf"
                                onChange={(e) => setFile(e.target.files[0])}
                            />
                            <div className="mt-2">
                                <small className="text-muted">
                                    <strong>Supported formats:</strong> Excel (.xlsx, .xls), CSV, or PDF<br />
                                    <strong>Required columns:</strong> Name, Email, Phone, Company (for Excel/CSV)<br />
                                    <strong>PDF requirements:</strong> Should contain extractable text with contact information
                                </small>
                            </div>
                        </FormGroup>
                    </Form>
                </ModalBody>
                <ModalFooter>
                    <Button color="secondary" onClick={() => {
                        setImportModal(false); // Close the modal
                        setFile(null); // Reset the file selection
                    }}>
                        Cancel
                    </Button>
                    <Button
                        color="primary"
                        onClick={handleImport}
                        disabled={!file || importLoading}
                    >
                        {importLoading ? 'Importing...' : 'Import'}
                        {importLoading && <Spinner className="ms-1" size="sm">Loading...</Spinner>}
                    </Button>
                </ModalFooter>
            </Modal>
            <Toast ref={toast} />
        </React.Fragment>
    );
};

export default LeadsList;